import { Link, useLocation } from "react-router-dom";
import { LucideIcon } from "lucide-react";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

export interface LiquidNavLink {
  href: string;
  label: string;
  icon?: LucideIcon;
}

interface LiquidGlassNavProps {
  links: LiquidNavLink[];
  className?: string;
}

interface Rect {
  x: number;
  w: number;
}

/**
 * Premium "liquid glass" navigation: a translucent bubble glides between
 * hovered items, with a subtle radial highlight following the cursor.
 */
const LiquidGlassNav = ({ links, className = "" }: LiquidGlassNavProps) => {
  const location = useLocation();
  const navRef = useRef<HTMLElement | null>(null);
  const itemsRef = useRef<Array<HTMLAnchorElement | null>>([]);
  const bubbleRef = useRef<HTMLSpanElement | null>(null);

  const activeIndex = links.findIndex((link) => link.href === location.pathname);

  const rectFor = useCallback((index: number): Rect | null => {
    const nav = navRef.current;
    const el = itemsRef.current[index];
    if (!nav || !el) return null;
    const navBox = nav.getBoundingClientRect();
    const box = el.getBoundingClientRect();
    return { x: box.left - navBox.left, w: box.width };
  }, []);

  const moveTo = useCallback(
    (index: number, visible: boolean) => {
      const bubble = bubbleRef.current;
      const rect = rectFor(index);
      if (!bubble) return;

      if (rect) {
        bubble.style.transform = `translate3d(${rect.x}px, 0, 0)`;
        bubble.style.width = `${rect.w}px`;
      }
      bubble.style.opacity = visible && rect ? "1" : "0";

      // The first measurement is painted without travelling in from x=0.
      if (bubble.dataset.ready !== "true") {
        requestAnimationFrame(() => {
          if (bubble.isConnected) bubble.dataset.ready = "true";
        });
      }
    },
    [rectFor]
  );

  // Rest state: sit under the active route (dimmed) or hide.
  const settleToActive = useCallback(() => {
    if (activeIndex >= 0) moveTo(activeIndex, true);
    else {
      const bubble = bubbleRef.current;
      if (bubble) bubble.style.opacity = "0";
    }
  }, [activeIndex, moveTo]);

  useLayoutEffect(() => {
    settleToActive();
  }, [settleToActive]);

  useEffect(() => {
    const onResize = () => settleToActive();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [settleToActive]);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLElement>) => {
    const nav = navRef.current;
    if (!nav) return;
    const box = nav.getBoundingClientRect();
    nav.style.setProperty("--lg-x", `${e.clientX - box.left}px`);
    nav.style.setProperty("--lg-y", `${e.clientY - box.top}px`);
  }, []);

  const [hoverIndex, setHoverIndex] = useState(-1);

  return (
    <nav
      ref={navRef}
      onPointerMove={handlePointerMove}
      onPointerLeave={() => {
        setHoverIndex(-1);
        settleToActive();
      }}
      className={`liquid-nav relative hidden md:flex items-center gap-1 rounded-full px-1.5 py-1.5 ${className}`}
      aria-label="Navigation principale"
    >
      {/* liquid glass bubble */}
      <span
        ref={bubbleRef}
        aria-hidden="true"
        className="liquid-nav-bubble"
        style={{ opacity: 0 }}
      />

      {links.map((link, i) => {
        const active = link.href === location.pathname;
        return (
          <Link
            key={link.href}
            to={link.href}
            ref={(el) => {
              itemsRef.current[i] = el;
            }}
            onPointerEnter={() => {
              setHoverIndex(i);
              moveTo(i, true);
            }}
            onFocus={() => moveTo(i, true)}
            onBlur={settleToActive}
            className={`liquid-nav-item relative z-[1] flex items-center gap-1.5 rounded-full px-3.5 py-2 text-sm font-medium tracking-[-0.01em] transition-[color,transform,filter] duration-200 ease-soft ${
              active || hoverIndex === i
                ? "text-foreground"
                : "text-muted-foreground"
            }`}
            aria-current={active ? "page" : undefined}
          >
            {link.icon && <link.icon className="h-4 w-4" />}
            {link.label}
          </Link>
        );
      })}
    </nav>
  );
};

export default LiquidGlassNav;
